import { useState } from 'react'
import { useNavigate, useParams } from 'react-router'

import { HttpStatus } from 'enums'
import {
  InvestmentPreferenceAction,
  PortfolioLinkType,
  TradePreference,
} from 'types'
import {
  PortfolioFormPayload,
  UpdatePreferencesRequest,
} from 'types/portfolioTypes'

import { paveApi } from 'api'
import BreadcrumbBar from 'components/common/BreadcrumbBar/BreadcrumbBar'
import { PageLayout } from 'components/partials'
import ConfirmSaveFirstPreferencesModal from 'components/partials/Portfolio/ConfirmSaveFirstPreferencesModal'
import ConfirmTaxBudgetPreferencesModal from 'components/partials/Portfolio/ConfirmTaxBudgetPreferencesModal'
import TradePreferenceDialog from 'components/partials/Portfolio/PortfolioPreferences/TradePreferenceDialog'
import InvestmentPreferenceFormAndComparisonSections from 'components/partials/Portfolio/V2/InvestmentPreferenceFormAndComparisonSections'
import InvestmentPreferenceHeaderSection from 'components/partials/Portfolio/V2/InvestmentPreferenceHeaderSection'
import InvestmentPreferenceNavigationSection from 'components/partials/Portfolio/V2/InvestmentPreferenceNavigationSection'
import RequestSupportModal from 'components/partials/RequestSupportModal'
import { ERROR_MESSAGE_1 } from 'constants/content.constants'
import PDSFlexBox from 'designSystem/components/PDSFlexBox/PDSFlexBox'
import PDSText from 'designSystem/components/PDSText/PDSText'
import { SIZE } from 'designSystem/foundations/constants/size'
import { useCustomToast } from 'hooks/useCustomToast'
import {
  buildUpdatePreferencesRequest,
  getInvestmentPreferenceAction,
  getIsAccountDisconnected,
  getPortfolioLinkType,
} from 'utils/portfolioUtil'

function InvestmentPreferencePage() {
  const [showRequestSupportModal, setShowRequestSupportModal] = useState(false)
  const { externalId } = useParams()
  const navigate = useNavigate()

  // Queries
  const { useGetPortfolioQuery } = paveApi
  const { data: portfolio, isLoading: isPortfolioLoading } =
    useGetPortfolioQuery(externalId || '')
  const linkMethod = getPortfolioLinkType(portfolio)
  const isUnlinkedPortfolio = linkMethod === PortfolioLinkType.None

  const [updateInvestmentPlanPreferences, { isLoading: isEditLoading }] =
    paveApi.useUpdateInvestmentPlanPreferencesMutation()
  const isDisabled = isEditLoading
  const { showErrorToast, showSuccessToast, showViewOnlyDefaultToast } =
    useCustomToast()
  const [portfolioPayload, setPortfolioPayload] =
    useState<PortfolioFormPayload | null>(null)
  const [isTradeDialogOpen, setIsTradeDialogOpen] = useState(false)
  const [showSaveFirstPreferencesModal, setShowSaveFirstPreferencesModal] =
    useState(false)

  const [showTaxBudgetPreferencesModal, setShowTaxBudgetPreferencesModal] =
    useState(false)

  const { data: groupData, isLoading: isGroupLoading } =
    paveApi.useGetClientAccountQuery(portfolio?.clientAccountId || '', {
      skip: !portfolio?.clientAccountId,
    })

  const isPortfolioDisconnected = getIsAccountDisconnected(
    portfolio?.accountStatus
  )

  const breadcrumbs = [
    {
      name: 'Home Page',
      route: `/`,
    },
    {
      name: groupData?.clientAccount?.displayName || 'Group Overview',
      route: `/account/${portfolio?.clientAccountId || ''}`,
    },
    {
      name: portfolio?.displayName || 'Portfolio Overview',
      route: `/portfolio/${portfolio?.id || ''}/v2`,
    },
    {
      name: 'Investment Preferences',
      route: ``,
    },
  ]

  /**
   * Determines which request(s) to make based on selected form values
   */

  const submitEditPortfolioForm = async (
    portfolioPayload: PortfolioFormPayload
  ) => {
    // Ensure enableTaxOptimization is set to false for unlinked portfolios
    if (isUnlinkedPortfolio) {
      portfolioPayload.investmentPreference.enableTaxOptimization = false
    }

    if (!portfolio) {
      showErrorToast('Missing portfolio.', 'Please try again later.')
      return
    }

    // Perform request and handle success and error
    const performAction = async (
      updatePreferencesRequest: UpdatePreferencesRequest,
      action: InvestmentPreferenceAction,
      handleClose = true
    ) =>
      updateInvestmentPlanPreferences(updatePreferencesRequest)
        .unwrap()
        .then(() => {
          if (handleClose) {
            handleSuccessToast(action)
          }
        })
        .catch((e: any) => {
          const { error, errorDescription, errorMessage } = e?.data ?? {}

          if (e?.status === HttpStatus.FORBIDDEN) {
            showViewOnlyDefaultToast()
          } else if (e?.data?.displayMessage) {
            // TODO ONB-528: Replace deprecated displayMessage pattern
            const { displayMessage, displayDescription } = e.data
            showErrorToast(displayMessage, displayDescription)
          } else if (error) {
            showErrorToast(errorMessage, errorDescription)
          } else {
            showErrorToast('An error occurred updating the portfolio.')
          }
        })

    // Success toast handler
    const handleSuccessToast = (action: InvestmentPreferenceAction) => {
      switch (action) {
        case InvestmentPreferenceAction.EDIT: // An optimization preference was changed. This also covers potential changes to template ID.
        case InvestmentPreferenceAction.EDIT_AND_PATCH_AUTOMATION: // An optimization preference field was changed AND automation was changed.
          showSuccessToast(
            'Suggested trades generated',
            'A new set of suggested trades was successfully generated for your portfolio'
          )
          break

        // Automation AND template ID were changed without impacting
        // any optimization preferences
        case InvestmentPreferenceAction.PATCH_AUTOMATION_AND_TEMPLATE_ID:
        case InvestmentPreferenceAction.PATCH_AUTOMATION: // Just automation was changed.
        case InvestmentPreferenceAction.PATCH_TEMPLATE_ID: // Just the template ID was changed without impacting any optimization preferences.
          showSuccessToast(
            'Investment preferences updated',
            'This portfolio’s investment preferences have been successfully updated'
          )
          break
      }
      navigate(`/portfolio/${externalId}`)
    }

    const action: InvestmentPreferenceAction = getInvestmentPreferenceAction(
      portfolio,
      portfolioPayload
    )

    if (action === InvestmentPreferenceAction.NO_ACTION) {
      showErrorToast(ERROR_MESSAGE_1)
    } else {
      const updatePreferencesRequest = buildUpdatePreferencesRequest(
        portfolio,
        portfolioPayload
      )
      await performAction(updatePreferencesRequest, action)
    }
  }

  const submitPortfolioFormStep1 = async (
    formPortfolioPayload: PortfolioFormPayload
  ) => {
    // If taxable is off, no need to show tax budget confirmation.
    if (
      formPortfolioPayload.investmentPreference?.enableTaxOptimization === false
    ) {
      submitPortfolioFormStep2(formPortfolioPayload)
      return
    }
    if (!portfolio) {
      showErrorToast('Missing portfolio.', 'Please try again later.')
      return
    }
    setPortfolioPayload(formPortfolioPayload)
    setShowTaxBudgetPreferencesModal(true)
  }

  const submitPortfolioFormStep2 = async (
    formPortfolioPayload: PortfolioFormPayload
  ) => {
    if (!portfolio) {
      showErrorToast('Missing portfolio.', 'Please try again later.')
      return
    }
    setPortfolioPayload(formPortfolioPayload)

    // Open first-time modal if:
    // - existing preferences are incomplete
    // - automation is on and portfolio has not traded

    // Open trade preference dialog if:
    // - there is any update to the preferences
    // - automation is on and portfolio has traded
    if (
      !portfolio.isInvestmentPreferenceCompleted ||
      (formPortfolioPayload?.investmentPreference?.automation &&
        !portfolio.lastTradedAt)
    ) {
      setShowSaveFirstPreferencesModal(true)
    } else if (formPortfolioPayload.investmentPreference?.automation) {
      setIsTradeDialogOpen(true)
    } else {
      submitEditPortfolioForm(formPortfolioPayload)
    }
  }

  const submitPortfolioFromTradesDialog = (
    tradePreference: TradePreference
  ) => {
    portfolioPayload &&
      submitEditPortfolioForm({ ...portfolioPayload, tradePreference })
    setIsTradeDialogOpen(false)
  }

  const confirmSubmitPortfolioFirstTime = () => {
    if (portfolioPayload) {
      setShowSaveFirstPreferencesModal(false)
      submitEditPortfolioForm({
        ...portfolioPayload,

        // If portfolio has automation enabled, default to TradePreference.NOW
        tradePreference: portfolioPayload?.investmentPreference?.automation
          ? TradePreference.NOW
          : undefined,
      })
    }
  }

  const confirmTaxBudgetPreferences = () => {
    if (portfolioPayload) {
      setShowTaxBudgetPreferencesModal(false)
      submitPortfolioFormStep2(portfolioPayload)
    }
  }

  let content
  if (!portfolio) {
    content = (
      <PDSText>
        An error occured loading this portfolio's investment preference.
      </PDSText>
    )
  } else {
    content = (
      <PDSFlexBox flexDirection="column" gap={SIZE.s12}>
        <InvestmentPreferenceHeaderSection
          openRequestSupport={() => setShowRequestSupportModal(true)}
        />
        <PDSFlexBox gap={SIZE.gutter}>
          <InvestmentPreferenceNavigationSection />
          <InvestmentPreferenceFormAndComparisonSections
            initialPortfolio={portfolio}
            isDisabled={isDisabled}
            handleSubmitForm={submitPortfolioFormStep1}
            submitEditPortfolioForm={submitEditPortfolioForm}
          />
        </PDSFlexBox>
      </PDSFlexBox>
    )
  }

  const isLoading = isPortfolioLoading || isGroupLoading

  return (
    <PageLayout
      showNewLayout={true}
      showLoader={isLoading}
      subHeader={<BreadcrumbBar paths={breadcrumbs} />}
    >
      {content}
      <TradePreferenceDialog
        isOpen={isTradeDialogOpen}
        setIsOpen={setIsTradeDialogOpen}
        handleTradeImmediately={() =>
          submitPortfolioFromTradesDialog(TradePreference.NOW)
        }
        handleTradeOnCadence={() =>
          submitPortfolioFromTradesDialog(TradePreference.MONTHLY_CADENCE)
        }
      />
      <ConfirmSaveFirstPreferencesModal
        isAutomationOn={portfolioPayload?.investmentPreference?.automation}
        onClick={confirmSubmitPortfolioFirstTime}
        onClose={() => setShowSaveFirstPreferencesModal(false)}
        showModal={showSaveFirstPreferencesModal}
      />
      <ConfirmTaxBudgetPreferencesModal
        hasTaxBudgetSet={
          portfolioPayload?.investmentPreference?.taxBudget !== null
        }
        onClick={confirmTaxBudgetPreferences}
        onClose={() => setShowTaxBudgetPreferencesModal(false)}
        showModal={showTaxBudgetPreferencesModal}
      />
      <RequestSupportModal
        entryPoint="Investment Preference Page"
        isOpen={showRequestSupportModal}
        onClose={() => setShowRequestSupportModal(false)}
        portfolioId={portfolio?.id}
        daysUntilSuggestions={portfolio?.daysUntilSuggestions || 0}
        incompletePortfolio={
          portfolio?.value === 0 ||
          isPortfolioDisconnected ||
          !portfolio?.isInvestmentPreferenceCompleted
        }
      />
    </PageLayout>
  )
}

export default InvestmentPreferencePage
